import { type ReactNode, createContext, useCallback, useContext } from "react"

import { type StartTimerInput, type Timer, useTimers } from "@/hooks/use-timers"
import {
  ensureNotificationPermission,
  fireTimerAlert,
} from "@/lib/timer-alerts"

export type StartTimer = (input: StartTimerInput) => void

interface TimersContextValue {
  timers: Timer[]
  startTimer: StartTimer
  pause: (id: string) => void
  resume: (id: string) => void
  cancel: (id: string) => void
  timerFor: (sourceId: string) => Timer | undefined
}

const TimersContext = createContext<TimersContextValue | null>(null)

// Hosts the timer state above the page components, in the persistent layout, so
// running timers survive Inertia navigations between pages.
export function TimersProvider({ children }: { children: ReactNode }) {
  const { timers, start, pause, resume, cancel } = useTimers({
    onComplete: (timer) =>
      fireTimerAlert(
        timer.step !== undefined
          ? `Step ${timer.step} · ${timer.label}`
          : timer.label,
      ),
  })

  const startTimer = useCallback<StartTimer>(
    (input) => {
      void ensureNotificationPermission()
      start(input)
    },
    [start],
  )

  const timerFor = useCallback(
    (sourceId: string) => timers.find((timer) => timer.sourceId === sourceId),
    [timers],
  )

  const value: TimersContextValue = {
    timers,
    startTimer,
    pause,
    resume,
    cancel,
    timerFor,
  }

  return (
    <TimersContext.Provider value={value}>{children}</TimersContext.Provider>
  )
}

export function useTimersContext(): TimersContextValue {
  const context = useContext(TimersContext)
  if (!context) {
    throw new Error("useTimersContext must be used within a TimersProvider")
  }
  return context
}
